<template>
  <div class="client-stats-panel">
    <div class="text-subtitle-1 font-weight-bold mb-3">
      Статистика клиента{{ userName ? `: ${userName}` : '' }}
    </div>

    <!-- Фильтр периода (только в модалке участников, без внешних дат) -->
    <v-card
      v-if="!externalPeriod && showPeriodStatistics"
      outlined
      class="pa-3 mb-4"
    >
      <div class="text-body-2 font-weight-medium mb-2">
        Период для статистики
      </div>
      <v-row
        align="center"
        dense
      >
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-menu
            v-model="menuFrom"
            attach
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="localDateFrom"
                label="Дата от"
                readonly
                clearable
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
                @click:clear="localDateFrom = null"
              />
            </template>
            <v-date-picker
              v-model="localDateFrom"
              no-title
              @input="menuFrom = false"
            />
          </v-menu>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-menu
            v-model="menuTo"
            attach
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="localDateTo"
                label="Дата до"
                readonly
                clearable
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
                @click:clear="localDateTo = null"
              />
            </template>
            <v-date-picker
              v-model="localDateTo"
              no-title
              @input="menuTo = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn
            color="primary"
            depressed
            small
            @click="applyLocalPeriod"
          >
            Применить
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn
            color="error"
            depressed
            small
            @click="clearDateFilters"
          >
            Сбросить даты
          </v-btn>
        </v-col>
      </v-row>
      <v-row
        align="center"
        dense
        class="mt-1"
      >
        <v-col cols="12" sm="auto">
          <v-btn
            depressed
            small
            class="filter-btn-accent white--text"
            @click="applyPresetPrevQuarter"
          >
            Прошлый квартал
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn
            depressed
            small
            class="filter-btn-accent white--text"
            @click="applyPresetCurrentQuarter"
          >
            Текущий квартал
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn
            depressed
            small
            class="filter-btn-accent white--text"
            @click="applyPresetCurrentYear"
          >
            Текущий год
          </v-btn>
        </v-col>
      </v-row>
      <div
        v-if="periodLabel"
        class="mt-2 text--secondary text-body-2"
      >
        Период: <strong>{{ periodLabel }}</strong>
        <span v-if="periodIsDefault"> (прошлый квартал, по умолчанию)</span>
      </div>
      <v-alert
        v-if="periodDateError"
        type="warning"
        text
        dense
        class="mt-2 mb-0"
      >
        {{ periodDateError }}
      </v-alert>
      <div class="mt-2 text--secondary text-caption">
        Период влияет на статистику за период, запросы подарков и начисления баллов.
        Статус, привилегии и история по кварталам от периода не зависят.
      </div>
    </v-card>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-if="!loading">
      <v-alert
        v-if="yearlyError"
        type="warning"
        text
        dense
        class="mb-4"
      >
        {{ yearlyError }}
      </v-alert>

      <!-- Текущий статус и привилегии — GET /club_player/loyalty_status -->
      <v-card
        outlined
        class="pa-3 mb-4"
      >
        <div class="text-body-2 font-weight-medium mb-2">
          Текущий статус и привилегии
        </div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <div class="label">
              Статус за прошлый квартал
            </div>
            <div class="mt-1">
              {{ formatLoyaltyStatus(loyaltyStatus) }}
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="privilegeRows.length"
          dense
          class="mt-2"
        >
          <v-col
            v-for="row in privilegeRows"
            :key="row.type"
            cols="12"
            sm="6"
          >
            <div class="label">
              {{ row.label }}
            </div>
            <div class="mt-1">
              {{ row.remains }}
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- История статусов по кварталам — GET /club_player/loyalty_yearly_statuses -->
      <loyalty-status-history-block
        class="mb-4"
        :year="historyYear"
        :year-options="yearOptions"
        :items="yearlyStatusItems"
        :loading="yearlyLoading"
        @year-change="onYearChange"
      />
    </template>

    <!-- Периодная статистика — вне блока loyalty loading -->
    <club-player-period-statistics
      v-if="showPeriodStatistics"
      class="mb-4"
      :user-id="userId"
      :date-from="effectiveDateFrom"
      :date-to="effectiveDateTo"
      single-client
      embedded
      @loaded="onPeriodStatsLoaded"
    />

    <template v-if="!loading">
      <!-- Подарки — из GET /club_player/history -->
      <v-card
        outlined
        class="pa-3 mb-4"
      >
        <div class="text-body-2 font-weight-medium mb-2">
          Запросы подарков
        </div>
        <v-simple-table
          v-if="filteredGiftRequests.length"
          dense
        >
          <template #default>
            <thead>
              <tr>
                <th>Дата запроса</th>
                <th>Дата выдачи</th>
                <th>Подарок</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in filteredGiftRequests"
                :key="index"
              >
                <td>{{ item.created_at || '—' }}</td>
                <td>{{ item.completed_at || '—' }}</td>
                <td>{{ item.gift || '—' }}</td>
                <td>{{ item.status || '—' }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div
          v-else
          class="text--secondary text-body-2"
        >
          Запросов подарков нет
        </div>
      </v-card>

      <!-- Начисления баллов — из GET /club_player/history -->
      <v-card
        outlined
        class="pa-3"
      >
        <div class="text-body-2 font-weight-medium mb-2">
          История начисления баллов
        </div>
        <v-simple-table
          v-if="filteredPointsHistory.length"
          dense
        >
          <template #default>
            <thead>
              <tr>
                <th>Дата</th>
                <th>Тип</th>
                <th>Основание</th>
                <th class="text-right">
                  Баллы
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in filteredPointsHistory"
                :key="index"
              >
                <td>{{ item.date || '—' }}</td>
                <td>{{ item.type === 'UP' ? 'Начисление' : 'Списание' }}</td>
                <td>{{ formatPointsReason(item) }}</td>
                <td class="text-right">
                  {{ item.points_amount != null ? item.points_amount : '—' }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div
          v-else
          class="text--secondary text-body-2"
        >
          Начислений нет
        </div>
      </v-card>
    </template>
  </div>
</template>

<script>
import Api from '@/api/api'
import ClubPlayerPeriodStatistics from '@/views/club_player/ClubPlayerPeriodStatistics.vue'
import LoyaltyStatusHistoryBlock from '@/views/club_player/LoyaltyStatusHistoryBlock.vue'
import {
  buildYearOptions,
  extractLoyaltyStatus,
  formatLoyaltyStatus,
  getQuarterDates,
  minYearFromDateStrings,
  parseLoyaltyYearlyStatuses,
} from '@/views/club_player/loyaltyConstants'

const PRIVILEGE_LABELS = {
  free_loyalty_court_canceling: 'Бесплатные отмены аренды корта (остаток)',
  free_loyalty_activity_record_canceling: 'Бесплатные отмены тренировок (остаток)',
  free_loyalty_abonement_extending: 'Бесплатное продление абонементов (остаток)',
}

export default {
  name: 'ClubPlayerClientStatsPanel',
  components: {
    LoyaltyStatusHistoryBlock,
    ClubPlayerPeriodStatistics,
  },
  props: {
    /** user_id участника (не user_pk) */
    userId: {
      type: [Number, String],
      required: true,
    },
    userName: {
      type: String,
      default: '',
    },
    /** Статус из списка users_points, если loyalty_status API не вернул поле */
    fallbackLoyaltyStatus: {
      type: String,
      default: null,
    },
    /** Даты периода с родительской страницы (YYYY-MM-DD) */
    dateFrom: {
      type: String,
      default: null,
    },
    dateTo: {
      type: String,
      default: null,
    },
    /** Родитель управляет периодом — не показывать локальный фильтр */
    externalPeriod: {
      type: Boolean,
      default: false,
    },
    /** Скрыть встроенную периодную статистику (показывается на странице статистики отдельно) */
    showPeriodStatistics: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      yearlyLoading: false,
      yearlyError: null,
      loyaltyStatus: null,
      privilegeRows: [],
      historyYear: new Date().getFullYear(),
      yearlyStatusItems: [],
      yearOptions: buildYearOptions(),
      giftRequests: [],
      pointsHistory: [],
      localDateFrom: null,
      localDateTo: null,
      appliedDateFrom: null,
      appliedDateTo: null,
      menuFrom: false,
      menuTo: false,
      periodLabel: null,
      periodIsDefault: false,
      periodDateError: null,
    }
  },
  computed: {
    effectiveDateFrom() {
      if (this.externalPeriod) return this.dateFrom
      return this.appliedDateFrom
    },
    effectiveDateTo() {
      if (this.externalPeriod) return this.dateTo
      return this.appliedDateTo
    },
    filteredGiftRequests() {
      return this.filterByDate(this.giftRequests, 'created_at')
    },
    filteredPointsHistory() {
      return this.filterByDate(this.pointsHistory, 'date')
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler() {
        this.loadAll()
      },
    },
    fallbackLoyaltyStatus(val) {
      if (val && !this.loyaltyStatus) {
        this.loyaltyStatus = val
      }
    },
  },
  methods: {
    formatLoyaltyStatus,

    applyLocalPeriod() {
      const hasFrom = !!this.localDateFrom
      const hasTo = !!this.localDateTo
      if (hasFrom !== hasTo) {
        this.periodDateError = 'Укажите обе даты периода: «Дата от» и «Дата до».'
        return
      }
      this.periodDateError = null
      this.appliedDateFrom = this.localDateFrom
      this.appliedDateTo = this.localDateTo
    },

    clearDateFilters() {
      this.localDateFrom = null
      this.localDateTo = null
      this.appliedDateFrom = null
      this.appliedDateTo = null
      this.periodDateError = null
    },

    applyPresetPrevQuarter() {
      const { from, to } = getQuarterDates(-1)
      this.localDateFrom = from
      this.localDateTo = to
      this.appliedDateFrom = from
      this.appliedDateTo = to
      this.periodDateError = null
    },

    applyPresetCurrentQuarter() {
      const { from, to } = getQuarterDates(0)
      this.localDateFrom = from
      this.localDateTo = to
      this.appliedDateFrom = from
      this.appliedDateTo = to
      this.periodDateError = null
    },

    applyPresetCurrentYear() {
      const year = new Date().getFullYear()
      this.localDateFrom = `${year}-01-01`
      this.localDateTo = `${year}-12-31`
      this.appliedDateFrom = this.localDateFrom
      this.appliedDateTo = this.localDateTo
      this.periodDateError = null
    },

    onPeriodStatsLoaded(stats) {
      if (stats?.period) {
        this.periodLabel = stats.period.label
        this.periodIsDefault = !!stats.period.is_default
      }
    },

    async loadAll() {
      if (this.userId == null) return
      this.loading = true
      this.yearlyError = null
      try {
        const results = await Promise.allSettled([
          this.loadLoyaltyStatus(),
          this.loadYearlyStatuses(this.historyYear),
          this.loadHistory(),
        ])
        results.forEach((r, i) => {
          if (r.status === 'rejected') {
            const labels = ['loyalty_status', 'loyalty_yearly_statuses', 'history']
            console.error(`ClubPlayerClientStatsPanel: ${labels[i]}`, r.reason)
          }
        })
      } finally {
        this.loading = false
      }
    },

    async loadLoyaltyStatus() {
      const data = await Api.getClubPlayerLoyaltyStatus({ user_id: this.userId })
      this.loyaltyStatus = extractLoyaltyStatus(data, this.fallbackLoyaltyStatus)
      if (!this.loyaltyStatus && this.fallbackLoyaltyStatus) {
        this.loyaltyStatus = this.fallbackLoyaltyStatus
      }
      this.privilegeRows = this.parsePrivileges(data.loyalty ?? data.after?.loyalty ?? data)
    },

    async loadYearlyStatuses(year) {
      this.yearlyLoading = true
      this.yearlyError = null
      try {
        const data = await Api.getClubPlayerLoyaltyYearlyStatuses({
          user_id: this.userId,
          year,
        })
        if (data?.year != null) {
          this.historyYear = Number(data.year) || year
        }
        const resolvedYear = data?.year != null ? Number(data.year) || year : year
        this.yearlyStatusItems = parseLoyaltyYearlyStatuses(data, resolvedYear)
      } catch (e) {
        console.error(e)
        this.yearlyStatusItems = parseLoyaltyYearlyStatuses(null, year)
        const msg = e?.response?.data?.error || e?.response?.data?.detail
        this.yearlyError = typeof msg === 'string'
          ? msg
          : 'Не удалось загрузить историю статусов за выбранный год'
      } finally {
        this.yearlyLoading = false
      }
    },

    async loadHistory() {
      const data = await Api.getClubPlayerClientHistory(this.userId)
      this.giftRequests = data?.gift_requests || []
      this.pointsHistory = data?.points_history || []
      const dateStrings = [
        ...this.giftRequests.map(g => g.created_at),
        ...this.pointsHistory.map(p => p.date),
      ]
      const minY = minYearFromDateStrings(dateStrings)
      this.yearOptions = buildYearOptions(15, minY)
    },

    onYearChange(year) {
      this.historyYear = year
      this.loadYearlyStatuses(year)
    },

    parsePrivileges(loyalty) {
      const rows = []
      if (!loyalty) return rows
      if (Array.isArray(loyalty)) {
        loyalty.forEach(item => {
          const type = item.privilege_type ?? item.type
          if (type && PRIVILEGE_LABELS[type]) {
            rows.push({
              type,
              label: PRIVILEGE_LABELS[type],
              remains: item.remains != null ? item.remains : '—',
            })
          }
        })
        return rows
      }
      Object.keys(PRIVILEGE_LABELS).forEach(type => {
        if (loyalty[type] != null) {
          const val = loyalty[type]
          rows.push({
            type,
            label: PRIVILEGE_LABELS[type],
            remains: typeof val === 'object' ? (val.remains ?? '—') : val,
          })
        }
      })
      return rows
    },

    formatPointsReason(item) {
      if (item.yc_data) {
        const svc = item.yc_data.services?.[0]?.title || ''
        return `${item.yc_data.date || ''} ${svc}`.trim() || '—'
      }
      if (item.comment) return item.comment
      return '—'
    },

    filterByDate(items, dateField) {
      const from = this.effectiveDateFrom
      const to = this.effectiveDateTo
      if (!from || !to || !items?.length) return items || []
      return items.filter(item => {
        const raw = item[dateField]
        if (!raw) return false
        const day = String(raw).slice(0, 10)
        return day >= from && day <= to
      })
    },
  },
}
</script>

<style scoped>
.label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

.filter-btn-accent {
  background-color: #14b1ff !important;
}
</style>
